import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getCompassChatHistory, editActionPrompt, deleteAction } from "../services/compass.service";
import {
  ICompasAIChatHistoryResponse,
  ICompassMessage,
  ICompasAIChatAction,
  ICompasAIChatMessage,
} from "../types/compass.types";

/**
 * Hook to fetch compass chat history
 */
export const useCompassChatHistory = (documentId: string) => {
  return useQuery<ICompasAIChatHistoryResponse, Error>({
    queryKey: ["compass-chat-history", documentId],
    queryFn: () => getCompassChatHistory(documentId),
    enabled: !!documentId,
  });
};

/**
 * Hook to transform compass history to display messages
 */
export const useTransformCompassHistory = (
  history: ICompasAIChatHistoryResponse | undefined
): ICompassMessage[] => {
  if (!history?.history || history.history.length === 0) {
    return [];
  }

  const transformedMessages: ICompassMessage[] = [];
  let currentActions: ICompasAIChatAction[] = [];
  let lastMessageId: string | undefined;

  history.history.forEach((item) => {
    if (item.type === "message") {
      const messageItem = item as ICompasAIChatMessage;
      lastMessageId = messageItem.message_id;

      // Add any pending actions
      if (currentActions.length > 0) {
        transformedMessages.push({
          id: lastMessageId,
          isSender: false,
          canCopy: false,
          text: "",
          creation_date: "",
          isAction: true,
          actions: currentActions,
        });
        currentActions = [];
      }

      // Add user question
      transformedMessages.push({
        id: messageItem.message_id,
        isSender: true,
        canCopy: false,
        text: messageItem.question,
        creation_date: messageItem.creation_date,
      });

      // Add assistant response
      if (messageItem.response.text && messageItem.response.type !== "prompt_chain") {
        transformedMessages.push({
          id: messageItem.message_id,
          isSender: false,
          canCopy: true,
          text: messageItem.response.text,
          creation_date: messageItem.creation_date,
        });
      }
    } else if (item.type === "action") {
      currentActions.push(item as ICompasAIChatAction);
    }
  });

  // Add any remaining actions
  if (currentActions.length > 0) {
    transformedMessages.push({
      id: lastMessageId,
      isSender: false,
      canCopy: false,
      text: "",
      creation_date: "",
      isAction: true,
      actions: currentActions,
    });
  }

  return transformedMessages;
};

/**
 * Hook to edit action prompt
 */
export const useEditActionPrompt = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { actionId: string; contextId: string; prompt: string }>({
    mutationFn: ({ actionId, contextId, prompt }) => editActionPrompt(actionId, contextId, prompt),
    onSuccess: (_, variables) => {
      // Invalidate compass chat history to refresh
      queryClient.invalidateQueries({
        queryKey: ["compass-chat-history", variables.contextId],
      });
    },
    onError: (error) => {
      console.error("Failed to edit action prompt:", error);
    },
  });
};

/**
 * Hook to delete action
 */
export const useDeleteAction = () => {
  const queryClient = useQueryClient();

  return useMutation<any, Error, { actionId: string; contextId: string }>({
    mutationFn: ({ actionId, contextId }) => deleteAction(actionId, contextId),
    onSuccess: (_, variables) => {
      // Invalidate compass chat history to refresh
      queryClient.invalidateQueries({
        queryKey: ["compass-chat-history", variables.contextId],
      });
    },
    onError: (error) => {
      console.error("Failed to delete action:", error);
    },
  });
};

/**
 * Hook to invalidate compass chat history manually
 */
export const useInvalidateCompassChat = () => {
  const queryClient = useQueryClient();

  return (documentId: string) => {
    queryClient.invalidateQueries({
      queryKey: ["compass-chat-history", documentId],
    });
  };
};
